import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  AppBar,
  Toolbar,
  Menu,
  MenuItem,
  ListItemIcon, // <-- Added
  ListItemText, // <-- Added
  Divider,
  Snackbar, // <-- Added
  Alert, // <-- Added
  List, // <-- Added
  ListItemButton, // <-- Added
  Chip,
} from '@mui/material';
import {
  Add,
  Save,
  Download,
  Upload,
  MoreVert,
  Analytics,
  Logout, // <-- Added
  Insights,
  Assessment,
} from '@mui/icons-material';
import { WidgetConfig, WidgetType, DashboardConfig } from '../../types/widget';
import WidgetContainer from '../widgets/WidgetContainer';
import { ChatCoPilot } from '../copilot/ChatCoPilot';
import { widgetRegistry, WidgetRegistration } from './widgetRegistry';
import { useAuth } from '../../context/AuthContext'; // <-- Added

interface WidgetDashboardProps {
  dashboardConfig?: DashboardConfig;
  onSave?: (config: DashboardConfig) => void;
  onExport?: () => void;
  onImport?: (config: DashboardConfig) => void;
}

const WidgetDashboard: React.FC<WidgetDashboardProps> = ({
  dashboardConfig,
  onSave,
  onExport,
  onImport,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [widgets, setWidgets] = useState<WidgetConfig[]>([]);
  const [isAddWidgetOpen, setIsAddWidgetOpen] = useState(false);
  const [newWidgetType, setNewWidgetType] = useState<WidgetType>(
    Object.keys(widgetRegistry)[0] as WidgetType
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [copilotInitialPrompt, setCopilotInitialPrompt] = useState<string>('');

  // Read ?copilot= URL param and use it as the initial chat prompt
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const prompt = params.get('copilot');
    if (prompt) {
      setCopilotInitialPrompt(decodeURIComponent(prompt));
      navigate('/', { replace: true });
    }
  }, [location.search, navigate]);

  // Mock dashboard configuration
  const defaultConfig: DashboardConfig = {
    id: 'default-dashboard',
    userId: 'user-1',
    name: 'Production Dashboard',
    widgets: [],
    layout: {
      columns: 12,
      rows: 8,
      gap: 16,
      padding: 16,
    },
    theme: {
      mode: 'dark',
      primaryColor: '#4299E1',
      backgroundColor: '#1A202C',
      cardBackgroundColor: '#2D3748',
      textColor: '#FFFFFF',
      accentColor: '#4299E1',
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  };

  const currentConfig = dashboardConfig || defaultConfig;

  useEffect(() => {
    setWidgets(currentConfig.widgets);
  }, [currentConfig]);

  const { timingTowerWidget, velocityWidget, driftWidget, sceneWidget, documentLibraryWidget, otherWidgets } = useMemo(() => {
    const tower = widgets.find((w) => w.type === WidgetType.TIMING_TOWER);
    const velocity = widgets.find((w) => w.type === WidgetType.PRODUCTION_VELOCITY);
    const drift = widgets.find((w) => w.type === WidgetType.DRIFT_ANALYSIS);
    const scene = widgets.find((w) => w.type === WidgetType.SCENE_ANALYSIS);
    const docLib = widgets.find((w) => w.type === WidgetType.DOCUMENT_LIBRARY);
    const others = widgets.filter((w) =>
      w.type !== WidgetType.TIMING_TOWER &&
      w.type !== WidgetType.PRODUCTION_VELOCITY &&
      w.type !== WidgetType.DRIFT_ANALYSIS &&
      w.type !== WidgetType.SCENE_ANALYSIS &&
      w.type !== WidgetType.DOCUMENT_LIBRARY
    );
    return {
      timingTowerWidget: tower,
      velocityWidget: velocity,
      driftWidget: drift,
      sceneWidget: scene,
      documentLibraryWidget: docLib,
      otherWidgets: others,
    };
  }, [widgets]);

  const { logout, user } = useAuth(); // <-- Added auth hook

  // --- MODIFIED: handleAddWidget ---
  const handleAddWidget = () => {
    // 1. Show soft warning if widget count is high
    if (otherWidgets.length >= 4) {
      setIsWarningOpen(true);
    }

    // 2. Get metadata from our new registry
    const widgetInfo = widgetRegistry[newWidgetType];
    if (!widgetInfo) return;

    // 3. Create the new widget
    const newWidget: WidgetConfig = {
      id: `widget-${Date.now()}`,
      type: newWidgetType,
      title: widgetInfo.title, // Title now comes from registry
      position: {
        x: 0,
        y: 0,
        z: widgets.length,
      },
      size: widgetInfo.defaultSize, // Size now comes from registry
      settings: {},
      isVisible: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    setWidgets([...widgets, newWidget]);
    setIsAddWidgetOpen(false);
  };
  // --- END MODIFICATION ---

  const handleUpdateWidget = (updatedWidget: WidgetConfig) => {
    setWidgets(widgets.map((w) => (w.id === updatedWidget.id ? updatedWidget : w)));
  };

  const handleDeleteWidget = (widgetId: string) => {
    setWidgets(widgets.filter((w) => w.id !== widgetId));
  };

  const handleDuplicateWidget = (widget: WidgetConfig) => {
    const duplicatedWidget: WidgetConfig = {
      ...widget,
      id: `widget-${Date.now()}`,
      title: `${widget.title} (Copy)`,
      position: {
        ...widget.position,
        z: widgets.length,
      },
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    setWidgets([...widgets, duplicatedWidget]);
  };

  const handleSave = () => {
    const updatedConfig: DashboardConfig = {
      ...currentConfig,
      widgets,
      updatedAt: new Date(),
    };
    onSave?.(updatedConfig);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* AppBar (Keep) */}
      <AppBar
        position="static"
        sx={{
          backgroundColor: '#2D3748',
          borderBottom: '1px solid #4A5568',
        }}
      >
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Analytics
              sx={{ mr: 2, color: currentConfig.theme.primaryColor }}
            />
            <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', color: 'white' }}>
              {process.env.REACT_APP_PRODUCTION_NAME || 'FilmiqAi'}
            </Typography>
            <Typography
              variant="caption"
              component="div"
              sx={{
                ml: 2,
                pt: 0.5,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                color: 'text.secondary',
                opacity: 0.8
              }}
            >
              Production Engine
            </Typography>
          </Box>
          
          {/* Navigation */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mx: 4 }}>
            <Chip
              label="Dashboard"
              icon={<Analytics sx={{ fontSize: 18 }} />}
              sx={{
                backgroundColor: '#4299E1',
                color: 'white',
                fontWeight: 600,
                '& .MuiChip-icon': { color: 'white' },
              }}
            />
            <Tooltip title="View Daily Insights">
              <Chip
                label="Insights"
                icon={<Insights sx={{ fontSize: 18 }} />}
                onClick={() => navigate('/insights')}
                clickable
                sx={{
                  backgroundColor: 'transparent',
                  color: 'white',
                  border: '1px solid #4A5568',
                  fontWeight: 500,
                  '& .MuiChip-icon': { color: '#9F7AEA' },
                  '&:hover': {
                    backgroundColor: 'rgba(159, 122, 234, 0.1)',
                    borderColor: '#9F7AEA',
                  },
                }}
              />
            </Tooltip>
            <Tooltip title="Production Recap & Analysis">
              <Chip
                label="Recap"
                icon={<Assessment sx={{ fontSize: 18 }} />}
                onClick={() => navigate('/production-insights')}
                clickable
                sx={{
                  backgroundColor: 'transparent',
                  color: 'white',
                  border: '1px solid #4A5568',
                  fontWeight: 500,
                  '& .MuiChip-icon': { color: '#48BB78' },
                  '&:hover': {
                    backgroundColor: 'rgba(72, 187, 120, 0.1)',
                    borderColor: '#48BB78',
                  },
                }}
              />
            </Tooltip>
          </Box>
          
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {user && (
              <Box sx={{ mr: 1, display: { xs: 'none', md: 'block' } }}>
                <Typography variant="caption" display="block" sx={{ opacity: 0.7, textAlign: 'right', lineHeight: 1 }}>
                  Logged in as
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {user.email}
                </Typography>
              </Box>
            )}
            
            <Tooltip title="Logout">
              <IconButton color="inherit" onClick={() => logout()} sx={{ ml: 1 }}>
                <Logout fontSize="small" />
              </IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 1, my: 2, bgcolor: '#4A5568' }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: 'success.main',
                  boxShadow: '0 0 8px 0px #48BB78',
                }}
              />
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                PROD: <span style={{ color: 'white', fontWeight: 'bold' }}>Telemetry</span>
              </Typography>
            </Box>
            <Tooltip title="Dashboard Options">
              <IconButton onClick={handleMenuOpen} sx={{ color: 'white' }}>
                <MoreVert />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Timing Tower + Main Workspace + Document Library */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexGrow: 1,
          height: 'calc(100vh - 64px)', // Full height minus AppBar
          overflow: 'hidden',
          backgroundColor: currentConfig.theme.backgroundColor,
        }}
      >
        {/* Column 1: Timing Tower (Permanent Left Sidebar) */}
        {timingTowerWidget && (
          <Box
            sx={{
              width: '350px',
              minWidth: '350px',
              flexShrink: 0,
              borderRight: '1px solid #4A5568',
              height: '100%',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              '&::-webkit-scrollbar': { width: '8px' },
              '&::-webkit-scrollbar-track': { backgroundColor: '#1A202C' },
              '&::-webkit-scrollbar-thumb': { backgroundColor: '#4A5568', borderRadius: '4px' },
            }}
          >
            <WidgetContainer
              widget={timingTowerWidget}
              onUpdate={handleUpdateWidget}
              onDelete={handleDeleteWidget}
              onDuplicate={handleDuplicateWidget}
            />
          </Box>
        )}

        {/* Column 2: Main Workspace */}
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflowY: 'auto',
            minWidth: 0,
          }}
        >
          {/* Row 1: Chat Panel (Fixed Height) */}
          <Box
            sx={{
              height: '550px',
              minHeight: '550px',
              flexShrink: 0,
              borderBottom: '1px solid #4A5568',
            }}
          >
            <ChatCoPilot initialPrompt={copilotInitialPrompt} />
          </Box>

          {/* Row 2: Production Velocity */}
          {velocityWidget && (
            <Box
              sx={{
                p: 2,
                borderBottom: '1px solid #4A5568',
                backgroundColor: currentConfig.theme.backgroundColor,
                minHeight: '420px',
                flexShrink: 0,
              }}
            >
              <WidgetContainer
                widget={velocityWidget}
                onUpdate={handleUpdateWidget}
                onDelete={handleDeleteWidget}
                onDuplicate={handleDuplicateWidget}
              />
            </Box>
          )}

          {/* Row 3: Fixed Analysis Widgets (Drift & Scene) */}
          {(driftWidget || sceneWidget) && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', xl: 'row' },
                gap: '20px',
                p: 2,
                borderBottom: '1px solid #4A5568',
                backgroundColor: currentConfig.theme.backgroundColor,
              }}
            >
              {driftWidget && (
                <Box sx={{ flex: 1, minWidth: 0, minHeight: '400px' }}>
                  <WidgetContainer
                    widget={driftWidget}
                    onUpdate={handleUpdateWidget}
                    onDelete={handleDeleteWidget}
                    onDuplicate={handleDuplicateWidget}
                  />
                </Box>
              )}
              {sceneWidget && (
                <Box sx={{ flex: 1, minWidth: 0, minHeight: '400px' }}>
                  <WidgetContainer
                    widget={sceneWidget}
                    onUpdate={handleUpdateWidget}
                    onDelete={handleDeleteWidget}
                    onDuplicate={handleDuplicateWidget}
                  />
                </Box>
              )}
            </Box>
          )}

          {/* Row 3: Dynamic Widgets Grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
              gridAutoRows: 'minmax(400px, auto)',
              gap: '20px',
              p: 2,
            }}
          >
            {otherWidgets.map((widget) => (
              <Box
                key={widget.id}
                sx={{
                  width: '100%',
                  height: '100%',
                  minHeight: '400px',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <WidgetContainer
                  widget={widget}
                  onUpdate={handleUpdateWidget}
                  onDelete={handleDeleteWidget}
                  onDuplicate={handleDuplicateWidget}
                />
              </Box>
            ))}
            
            {otherWidgets.length === 0 && (
              <Box sx={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', p: 4 }}>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => setIsAddWidgetOpen(true)}
                  sx={{ color: '#4A5568', borderColor: '#4A5568' }}
                >
                  Add More Widgets
                </Button>
              </Box>
            )}
          </Box>
        </Box>

        {/* Column 3: Document Library (Permanent Right Sidebar) */}
        {documentLibraryWidget && (
          <Box
            sx={{
              width: '350px',
              minWidth: '350px',
              flexShrink: 0,
              borderLeft: '1px solid #4A5568',
              height: '100%',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              '&::-webkit-scrollbar': { width: '8px' },
              '&::-webkit-scrollbar-track': { backgroundColor: '#1A202C' },
              '&::-webkit-scrollbar-thumb': { backgroundColor: '#4A5568', borderRadius: '4px' },
            }}
          >
            <WidgetContainer
              widget={documentLibraryWidget}
              onUpdate={handleUpdateWidget}
              onDelete={handleDeleteWidget}
              onDuplicate={handleDuplicateWidget}
            />
          </Box>
        )}
      </Box>

      {/* Add Widget FAB (Keep) */}
      <Fab
        color="primary"
        aria-label="add widget"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          backgroundColor: currentConfig.theme.primaryColor,
          '&:hover': {
            backgroundColor: currentConfig.theme.accentColor,
          },
        }}
        onClick={() => setIsAddWidgetOpen(true)}
      >
        <Add />
      </Fab>

      {/* --- MODIFIED: "Add Widget" Dialog --- */}
      <Dialog
        open={isAddWidgetOpen}
        onClose={() => setIsAddWidgetOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: currentConfig.theme.cardBackgroundColor,
            color: currentConfig.theme.textColor,
          },
        }}
      >
        <DialogTitle>Add New Widget</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a widget to add to your dashboard.
          </Typography>
          <List sx={{ maxHeight: 400, overflowY: 'auto' }}>
            {Object.values(widgetRegistry).map((widget: WidgetRegistration) => {
              // Don't allow adding permanent widgets that already exist
              if (
                (widget.type === WidgetType.TIMING_TOWER && timingTowerWidget) ||
                (widget.type === WidgetType.DRIFT_ANALYSIS && driftWidget) ||
                (widget.type === WidgetType.SCENE_ANALYSIS && sceneWidget) ||
                (widget.type === WidgetType.DOCUMENT_LIBRARY && documentLibraryWidget)
              ) {
                return null;
              }

              const isSelected = newWidgetType === widget.type;
              return (
                <ListItemButton
                  key={widget.type}
                  selected={isSelected}
                  onClick={() => setNewWidgetType(widget.type)}
                  sx={{
                    mb: 1,
                    borderRadius: '8px',
                    border: '1px solid',
                    borderColor: isSelected ? 'primary.main' : 'transparent',
                    backgroundColor: isSelected
                      ? 'rgba(66, 153, 225, 0.1)'
                      : 'rgba(255, 255, 255, 0.05)',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>
                    <widget.icon />
                  </ListItemIcon>
                  <ListItemText
                    primary={widget.title}
                    secondary={widget.description}
                    primaryTypographyProps={{ fontWeight: 'bold' }}
                    secondaryTypographyProps={{
                      color: 'text.secondary',
                      fontSize: '0.8rem',
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddWidgetOpen(false)}>Cancel</Button>
          <Button onClick={handleAddWidget} variant="contained">
            Add Widget
          </Button>
        </DialogActions>
      </Dialog>
      {/* --- END MODIFICATION --- */}

      {/* --- NEW: Soft Warning Snackbar --- */}
      <Snackbar
        open={isWarningOpen}
        autoHideDuration={6000}
        onClose={() => setIsWarningOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setIsWarningOpen(false)}
          severity="info"
          sx={{ width: '100%' }}
        >
          Heads up: Adding more widgets may impact readability.
        </Alert>
      </Snackbar>
      {/* --- END NEW --- */}

      {/* Dashboard Options Menu (Keep) */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            backgroundColor: currentConfig.theme.cardBackgroundColor,
            color: currentConfig.theme.textColor,
          },
        }}
      >
        <MenuItem onClick={handleSave}>
          <ListItemIcon>
            <Save />
          </ListItemIcon>
          <ListItemText>Save Dashboard</ListItemText>
        </MenuItem>
        <MenuItem onClick={onExport}>
          <ListItemIcon>
            <Download />
          </ListItemIcon>
          <ListItemText>Export Configuration</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => console.log('Import configuration')}>
          <ListItemIcon>
            <Upload />
          </ListItemIcon>
          <ListItemText>Import Configuration</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default WidgetDashboard;
